import React, { useCallback, useState } from "react";
import {
  SaveButton,
  Toolbar,
  useCreate,
  useUpdate,
  useNotify,
  useRedirect,
} from "react-admin";
import { useForm } from "react-final-form";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useVideoUpload } from "./VideoUploadContext";
import {
  uploadTrainingVideoToS3,
  VIDEO_UPLOAD_SOURCES,
  PENDING_VIDEO_VALUE,
  getUploadErrorMessage,
} from "./videoUpload";
import { rowsToApiTags } from "./util";

const UPLOAD_LABELS = {
  video_file_path: "Uploading video...",
  vietnamese_video_file_path: "Uploading Vietnamese video...",
};

const sanitizePayload = values => {
  const payload = { ...values };

  VIDEO_UPLOAD_SOURCES.forEach(source => {
    if (payload[source] === PENDING_VIDEO_VALUE) {
      delete payload[source];
    }
  });

  return payload;
};

export const uploadPendingTrainingVideos = async ({
  form,
  getPendingFile,
  clearPendingFile,
  setUploadError,
  clearUploadError,
  setUploadProgress,
  clearUploadProgress,
  onProgress,
}) => {
  const values = { ...form.getState().values };

  for (const source of VIDEO_UPLOAD_SOURCES) {
    const file = getPendingFile(source);
    if (!file) continue;

    if (onProgress) onProgress(UPLOAD_LABELS[source] || "Uploading video...", source);
    if (setUploadProgress) setUploadProgress(source, 0);

    try {
      const key = await uploadTrainingVideoToS3(file, percent => {
        if (setUploadProgress) setUploadProgress(source, percent);
        if (onProgress) {
          onProgress(
            `${UPLOAD_LABELS[source] || "Uploading video..."} ${percent}%`,
            source,
            percent
          );
        }
      });
      form.change(source, key);
      values[source] = key;
      clearPendingFile(source);
      if (clearUploadError) clearUploadError(source);
      if (clearUploadProgress) clearUploadProgress(source);
    } catch (error) {
      if (clearUploadProgress) clearUploadProgress(source);
      form.change(source, PENDING_VIDEO_VALUE);
      if (setUploadError) {
        setUploadError(source, getUploadErrorMessage(error));
      }
      throw error;
    }
  }

  return values;
};

export const DeferredSaveButton = ({
  mode = "edit",
  resource = "training-videos",
  record,
  basePath,
  ...props
}) => {
  const form = useForm();
  const notify = useNotify();
  const redirect = useRedirect();
  const [create] = useCreate(resource);
  const [update] = useUpdate(resource);
  const {
    pendingFiles,
    getPendingFile,
    clearPendingFile,
    setUploadError,
    clearUploadError,
    clearAllUploadErrors,
    setUploadProgress,
    clearUploadProgress,
    clearAllUploadProgress,
  } = useVideoUpload();
  const [saving, setSaving] = useState(false);
  const [statusLabel, setStatusLabel] = useState("");

  const resetSaving = useCallback(() => {
    setSaving(false);
    setStatusLabel("");
    clearAllUploadProgress();
  }, [clearAllUploadProgress]);

  const handleClick = useCallback(async () => {
    const { values, hasValidationErrors } = form.getState();
    const hasRequiredVideo =
      (values.video_file_path &&
        values.video_file_path !== PENDING_VIDEO_VALUE) ||
      pendingFiles.video_file_path;

    if (!hasRequiredVideo) {
      notify("Please select a video", { type: "warning" });
      return;
    }

    if (hasValidationErrors) return;

    setSaving(true);
    clearAllUploadErrors();
    clearAllUploadProgress();

    try {
      const updatedValues = await uploadPendingTrainingVideos({
        form,
        getPendingFile,
        clearPendingFile,
        setUploadError,
        clearUploadError,
        setUploadProgress,
        clearUploadProgress,
        onProgress: label => setStatusLabel(label),
      });

      if (form.getState().hasValidationErrors) {
        notify("Please fix form errors before saving", { type: "warning" });
        resetSaving();
        return;
      }

      const payload = sanitizePayload(updatedValues);
      const apiPayload = {
        ...payload,
        tags: rowsToApiTags(payload.tags),
      };

      if (mode === "create") {
        create(
          {
            payload: { data: apiPayload },
          },
          {
            onSuccess: () => {
              resetSaving();
              redirect(basePath || `/${resource}`);
            },
            onFailure: error => {
              resetSaving();
              const errors = error?.response?.data?.errors;
              const message = errors
                ? errors[Object.keys(errors)[0]][0]
                : error?.response?.data?.message || "Save failed";
              notify(message, { type: "warning" });
            },
          }
        );
        return;
      }

      if (!record?.id) {
        notify("Record not found", { type: "warning" });
        resetSaving();
        return;
      }

      update(
        resource,
        record.id,
        apiPayload,
        record,
        {
          onSuccess: () => {
            resetSaving();
            redirect(basePath || `/${resource}`);
          },
          onFailure: error => {
            resetSaving();
            const errors = error?.response?.data?.errors;
            const message = errors
              ? errors[Object.keys(errors)[0]][0]
              : error?.response?.data?.message || "Save failed";
            notify(message, { type: "warning" });
          },
        }
      );
    } catch (error) {
      notify(getUploadErrorMessage(error), { type: "warning" });
      resetSaving();
    }
  }, [
    form,
    pendingFiles,
    getPendingFile,
    clearPendingFile,
    setUploadError,
    clearUploadError,
    clearAllUploadErrors,
    setUploadProgress,
    clearUploadProgress,
    clearAllUploadProgress,
    notify,
    mode,
    create,
    update,
    redirect,
    resource,
    record,
    basePath,
    resetSaving,
  ]);

  return (
    <SaveButton
      {...props}
      saving={saving}
      label={saving ? statusLabel || "Saving..." : props.label}
      icon={saving ? <CircularProgress size={18} /> : props.icon}
      disabled={saving || form.getState().hasValidationErrors}
      handleSubmitWithRedirect={handleClick}
    />
  );
};

export const TrainingVideoCreateToolbar = props => (
  <Toolbar {...props}>
    <DeferredSaveButton {...props} mode="create" />
  </Toolbar>
);
